import {
  FormCard,
  FormCardBlock,
  FormCardRow,
} from "@/components/ux/form.card"
import { useWebhook } from "@/api/webhook"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { useForm } from "react-hook-form"
import { webhookSchema } from "./webhook.schema"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useEffect, useState } from "react"
import { toast } from "sonner"

export default function WebhookForm() {
  const { webhook, createWebhook, deleteWebhook, testWebhook } = useWebhook()
  const [isTesting, setIsTesting] = useState(false)

  const form = useForm<z.infer<typeof webhookSchema>>({
    resolver: zodResolver(webhookSchema),
    defaultValues: {
      url: webhook?.url || "",
      secret: webhook?.secret || "",
    },
  })

  useEffect(() => {
    if (webhook) {
      form.reset({
        url: webhook.url,
        secret: webhook.secret,
      })
    }
  }, [webhook, form])

  const onSubmit = async (data: z.infer<typeof webhookSchema>) => {
    try {
      await createWebhook(data)
      toast.success("Webhook configuration saved")
    } catch (error) {
      toast.error("Failed to save webhook configuration")
    }
  }

  const onTest = async () => {
    setIsTesting(true)
    try {
      await testWebhook()
      toast.success("Webhook test successful")
    } catch (error) {
      toast.error("Webhook test failed")
    } finally {
      setIsTesting(false)
    }
  }

  const onDelete = async () => {
    try {
      await deleteWebhook()
      toast.success("Webhook deleted")
    } catch (error) {
      toast.error("Failed to delete webhook")
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormCard
          title="Webhook Configuration"
          actions={
            <div className="flex gap-2">
              {webhook && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onTest}
                    disabled={isTesting}
                  >
                    {isTesting ? "Testing..." : "Test Webhook"}
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={onDelete}
                  >
                    Delete
                  </Button>
                </>
              )}
              <Button type="submit">Save</Button>
            </div>
          }
        >
          <FormCardBlock>
            <FormField
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormCardRow>
                    <FormLabel required>Webhook URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://your-domain.com/webhook"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormCardRow>
                </FormItem>
              )}
            />
            <FormField
              name="secret"
              render={({ field }) => (
                <FormItem>
                  <FormCardRow>
                    <FormLabel required>Webhook Secret</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Your webhook secret"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormCardRow>
                </FormItem>
              )}
            />
          </FormCardBlock>
        </FormCard>
      </form>
    </Form>
  )
}
